/* Morpheus: capture published promotion evidence. No checkout interception,
 * quantities, prices, customer details or fulfillment operations. */
(() => {
  if (window.__morpheusKoalaCaptureV1) return;
  window.__morpheusKoalaCaptureV1 = true;
  const script = document.currentScript;
  if (!script || !script.src) return;
  const endpoint = script.dataset.morpheusKoalaEndpoint || new URL('/api/public/koala/receipt', script.src).href;
  const root = window.Shopify?.routes?.root || '/';
  const attribute = '__morpheus_koala_receipt';
  const contextStorageKey = 'morpheus.koala.page-context.v1';
  const contextLifetimeMs = 30 * 60 * 1000;
  let running = false;
  let last = '';
  let next = 0;
  let failures = 0;
  let stopped = false;

  function pageBundles() {
    return JSON.parse(JSON.stringify(Object.values(window.ProfitKoala || {}).filter(value => value?.product && Array.isArray(value.offers))
      .map(value => ({ product: value.product, shop: value.shop, deal: value.deal, offers: value.offers })), (_key, value) => value === undefined ? null : value));
  }

  function rememberedContexts() {
    try {
      const raw = sessionStorage.getItem(contextStorageKey);
      if (!raw || raw.length > 200000) return [];
      const contexts = JSON.parse(raw);
      if (!Array.isArray(contexts) || contexts.length > 10) return [];
      const now = Date.now();
      return contexts.filter(context => Number.isFinite(context.at) && context.at <= now && now - context.at < contextLifetimeMs
        && typeof context.bundle?.product?.handle === 'string' && Array.isArray(context.bundle.offers));
    } catch { return []; }
  }

  function rememberPage() {
    const bundles = pageBundles();
    const contexts = new Map(rememberedContexts().map(context => [context.bundle.product.handle, context]));
    for (const bundle of bundles) {
      contexts.delete(bundle.product.handle);
      contexts.set(bundle.product.handle, { at: Date.now(), bundle });
    }
    let saved = [...contexts.values()].slice(-10);
    while (saved.length && JSON.stringify(saved).length > 200000) saved = saved.slice(1);
    try { sessionStorage.setItem(contextStorageKey, JSON.stringify(saved)); } catch { /* Missing storage keeps review conservative. */ }
    return bundles;
  }

  function observation() {
    // 2026-09-16: product -> cart navigation loses ProfitKoala page data
    // (FINDINGS: Koala page context). Cache only published definitions, never cart
    // tokens/customer data. They are still untrusted until the server rereads them.
    const bundles = rememberPage();
    const states = [...document.querySelectorAll('koala-gifts[data-state], koala-gifts-banners[data-state]')].map(element => {
      const bytes = Uint8Array.from(atob(element.getAttribute('data-state')), char => char.charCodeAt(0));
      const state = JSON.parse(new TextDecoder().decode(bytes));
      return { market: state.market, offers: state.offers };
    });
    if (states.some(state => JSON.stringify(state) !== JSON.stringify(states[0]))) throw new Error('Conflicting gift contexts');
    if (!states.length) throw new Error('Promotion context not loaded');
    return JSON.parse(JSON.stringify({ bundles, gifts: states[0] }, (_key, value) => value === undefined ? null : value));
  }

  function basket(cart) {
    // Ignore private attributes, per-read discount UUIDs, tracking and timestamps.
    return JSON.stringify({ token: cart.token, currency: cart.currency, total: cart.total_price,
      coupons: (cart.cart_level_discount_applications || []).map(discount => [discount.type, discount.title,
        discount.value_type, discount.value, discount.allocation_method, discount.target_selection,
        discount.target_type, discount.total_allocated_amount]).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
      items: cart.items.map(item => ({ variant: item.variant_id, quantity: item.quantity,
        price: item.original_price, final: item.final_line_price, properties: item.properties,
        discounts: item.line_level_discount_allocations.map(discount => [discount.amount, discount.discount_application.title]) })) });
  }

  async function readCart() {
    const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12000) });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  }

  async function capture() {
    if (stopped || running || document.hidden || Date.now() < next) return;
    running = true;
    try {
      const observed = observation();
      const cart = await readCart();
      if (!cart.items.length) { last = ''; return; }
      const handles = new Set(cart.items.map(item => item.handle));
      const bundles = new Map(rememberedContexts().filter(context => handles.has(context.bundle.product.handle))
        .map(context => [context.bundle.product.handle, context.bundle]));
      for (const bundle of observed.bundles) bundles.set(bundle.product.handle, bundle);
      observed.bundles = [...bundles.values()];
      const fingerprint = basket(cart);
      // A widget can finish loading after the first cart read. Recheck new
      // published context even when quantities/prices did not change.
      const contextKey = JSON.stringify({ ...observed, bundles: observed.bundles.map(bundle => ({ ...bundle,
        offers: bundle.offers.map(({ selected, variants, ...terms }) => terms) })) });
      const capturedKey = fingerprint + contextKey;
      if (capturedKey === last) return;
      const response = await fetch(endpoint, { method: 'POST', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: cart.token, observation: observed }),
        signal: AbortSignal.timeout(60000) });
      if (!response.ok) throw new Error('Receipt unavailable');
      const result = await response.json();
      if (!/^v1\.[a-f0-9-]{36}\.[a-f0-9]{64}$/.test(result.receipt)) throw new Error('Invalid receipt');
      if (basket(await readCart()) !== fingerprint) return;
      const saved = await fetch(`${root}cart/update.js`, { method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { [attribute]: result.receipt } }),
        signal: AbortSignal.timeout(12000) });
      if (!saved.ok) throw new Error('Receipt attribute unavailable');
      last = capturedKey;
      failures = 0;
      next = Date.now() + 5000;
    } catch {
      failures++;
      next = Date.now() + Math.min(120000, 5000 * 2 ** Math.min(failures, 5));
    } finally { running = false; }
  }

  const timer = setInterval(capture, 5000);
  document.addEventListener('visibilitychange', capture);
  window.addEventListener('pageshow', () => { stopped = false; capture(); });
  window.addEventListener('pagehide', event => {
    try { rememberPage(); } catch { /* Backend review covers unavailable page context. */ }
    if (!event.persisted) { stopped = true; clearInterval(timer); }
  });
  capture();
})();
